import React, { useState, useEffect } from 'react'
import {
  faEye,
  faEyeSlash,
  faFileExcel,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import dayjs from 'dayjs'
import InputMask from 'react-input-mask'
import { maskCpfCnpj, maskPhone } from '../utils/maskUtils'
import { parseCivilDate } from '../utils/dateUtils'
import {
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
} from '@mui/material'
import StyledTextField from './shared/StyledTextField'
import { NumericFormat } from 'react-number-format'

// ===== Helpers de data (sem DatePicker) =====
const isDateTimeType = (type, withHours, useDateTime) =>
  type === 'date' && (withHours || useDateTime)

const dateMaskFor = (type, withHours, useDateTime) =>
  type === 'month'
    ? '99/9999'
    : isDateTimeType(type, withHours, useDateTime)
      ? '99/99/9999 99:99'
      : '99/99/9999'

// Valor salvo (YYYY-MM-DD) → texto exibido (DD/MM/YYYY)
const toDateDisplay = (value, type, withHours, useDateTime) => {
  if (!value) return ''
  if (type === 'month') return value
  const d = dayjs(value)
  if (!d.isValid()) return ''
  return isDateTimeType(type, withHours, useDateTime)
    ? d.format('DD/MM/YYYY HH:mm')
    : d.format('DD/MM/YYYY')
}

// Texto digitado (DD/MM/YYYY) → valor salvo (YYYY-MM-DD)
const toDateStorage = (text, type, withHours, useDateTime) => {
  if (type === 'month') {
    return /^\d{2}\/\d{4}$/.test(text) ? text : null
  }

  if (isDateTimeType(type, withHours, useDateTime)) {
    const m = text.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/)
    if (!m) return null
    const d = dayjs(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}:00`)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : null
  }

  const m = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  return parseCivilDate(text)
}

// Componente auxiliar para a máscara numérica no MUI
const NumericFormatCustom = React.forwardRef(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.floatValue,
            },
          })
        }}
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={4}
        fixedDecimalScale
        allowNegative={false}
      />
    )
  }
)

const IntegerFormatCustom = React.forwardRef(
  function IntegerFormatCustom(props, ref) {
    const { onChange, ...other } = props

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.floatValue,
            },
          })
        }}
        thousandSeparator="."
        decimalSeparator=","
        allowNegative={false}
        decimalScale={0}
      />
    )
  }
)

const CpfCnpjMaskCustom = React.forwardRef(
  function CpfCnpjMaskCustom(props, ref) {
    const { onChange, value, ...other } = props

    const handleChange = (e) => {
      let rawValue = e.target.value

      // Remove tudo que não é letra ou número e converte para maiúscula
      const cleanValue = rawValue.toUpperCase().replace(/[^A-Z0-9]/g, '')

      let formattedValue = ''

      // Se tiver até 11 caracteres E for apenas números, aplica máscara de CPF
      if (cleanValue.length <= 11 && /^\d*$/.test(cleanValue)) {
        const numericValue = cleanValue.replace(/[^0-9]/g, '').slice(0, 11)

        formattedValue = numericValue
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      } else {
        // CNPJ alfanumérico (até 14 caracteres)
        const cnpjValue = cleanValue.slice(0, 14)

        formattedValue = cnpjValue
          .replace(/^([A-Z0-9]{2})([A-Z0-9])/, '$1.$2')
          .replace(/^([A-Z0-9]{2})\.([A-Z0-9]{3})([A-Z0-9])/, '$1.$2.$3')
          .replace(
            /^([A-Z0-9]{2})\.([A-Z0-9]{3})\.([A-Z0-9]{3})([A-Z0-9])/,
            '$1.$2.$3/$4'
          )
          .replace(
            /^([A-Z0-9]{2})\.([A-Z0-9]{3})\.([A-Z0-9]{3})\/([A-Z0-9]{4})([A-Z0-9]{1,2})/,
            '$1.$2.$3/$4-$5'
          )
      }

      onChange({
        target: {
          name: other.name || props.name,
          value: formattedValue,
        },
      })
    }

    return (
      <input {...other} ref={ref} value={value || ''} onChange={handleChange} />
    )
  }
)

const PhoneMaskCustom = React.forwardRef(function PhoneMaskCustom(props, ref) {
  const { onChange, value, ...other } = props

  const handleChange = (e) => {
    const rawValue = e.target.value
    const formattedValue = maskPhone(rawValue)

    onChange({
      target: {
        name: other.name || props.name,
        value: formattedValue,
      },
    })
  }

  return (
    <input {...other} ref={ref} value={value || ''} onChange={handleChange} />
  )
})

const InputMaskCustom = React.forwardRef(function InputMaskCustom(props, ref) {
  const { onChange, ...other } = props
  const { mask, ...inputProps } = other

  return (
    <InputMask
      {...inputProps}
      inputRef={ref}
      mask={mask}
      maskChar={null}
      onChange={onChange}
    />
  )
})

const InputField = ({
  label,
  id,
  value,
  views,
  inputFormat,
  onChange,
  placeholder,
  type = 'text',
  error,
  disabled = false,
  readOnly = false,
  icon,
  mask,
  isNumericFormat = false,
  formatType = 'currency',
  maxLength = null,
  defaultValue,
  withHours = false,
  useDateTime = false,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  // ✅ Texto local do campo de data (digitação manual com máscara)
  const [dateText, setDateText] = useState('')

  // ✅ Sincroniza o texto quando o valor muda externamente (load da edição, reset, etc.)
  useEffect(() => {
    if (type !== 'date' && type !== 'month') return
    setDateText((current) =>
      toDateStorage(current, type, withHours, useDateTime) === value
        ? current
        : toDateDisplay(value, type, withHours, useDateTime)
    )
  }, [value, type, withHours, useDateTime])

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev)
  }

  if (type === 'checkbox') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            id={id}
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label={label}
      />
    )
  }

  // ✅ CAMPO DE DATA/MÊS: input com máscara, SEM DatePicker
  if (type === 'date' || type === 'month') {
    const completeCalendarDate = /^\d{2}\/\d{2}\/\d{4}$/.test(dateText)
    const invalidMaintenanceDate =
      id === 'maintenance.maintenance_date' &&
      type === 'date' &&
      completeCalendarDate &&
      !toDateStorage(dateText, type, withHours, useDateTime)

    return (
      <Box mb="4px">
        <StyledTextField
          id={id}
          label={label}
          value={dateText}
          onChange={(e) => {
            const text = e.target.value
            setDateText(text)

            if (text === '') {
              onChange({ target: { name: id, value: null } })
              return
            }

            const stored = toDateStorage(text, type, withHours, useDateTime)
            if (type === 'date' && !isDateTimeType(type, withHours, useDateTime)) {
              // Clear stale form state during partial or impossible date entry.
              onChange({ target: { name: id, value: stored } })
            } else if (stored) {
              onChange({ target: { name: id, value: stored } })
            }
          }}
          placeholder={placeholder}
          error={!!error || invalidMaintenanceDate}
          helperText={error || (invalidMaintenanceDate ? 'Informe uma data válida.' : '')}
          disabled={disabled}
          fullWidth
          size="small"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          InputProps={{
            inputComponent: InputMaskCustom,
            inputProps: {
              mask: dateMaskFor(type, withHours, useDateTime),
            },
          }}
        />
      </Box>
    )
  }

  if (type === 'file') {
    return (
      <Box mb="4px">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            my: 0.5,
            fontWeight: 'bold',
          }}>
          {icon && <FontAwesomeIcon icon={icon} style={{ marginRight: 8 }} />}
          {label}
        </Typography>

        <StyledTextField
          id={id}
          type="text"
          value={value ? value.name || '' : ''}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <label htmlFor={id} style={{ cursor: 'pointer' }}>
                  <input
                    id={id}
                    type="file"
                    hidden
                    accept=".xls, .xlsx"
                    onChange={onChange}
                    disabled={disabled}
                  />

                  <IconButton component="span" disabled={disabled}>
                    <FontAwesomeIcon icon={faFileExcel} />
                  </IconButton>
                </label>
              </InputAdornment>
            ),
          }}
          error={!!error}
          helperText={error}
          variant="outlined"
          fullWidth
          disabled={disabled}
        />
      </Box>
    )
  }

  const commonProps = {
    id,
    value: value !== undefined && value !== null ? value : '',
    onChange,
    placeholder,
    disabled,
    readOnly,
    error: !!error,
    helperText: error,
    variant: 'outlined',
    fullWidth: true,
    multiline: type === 'textarea',
    rows: type === 'textarea' ? 2 : 1,
    inputProps: {
      maxLength: maxLength,
      min: 1,
    },
  }

  // CONSTRUIR InputProps de forma incremental
  let inputPropsObject = {}

  // Adicionar o ícone se existir
  if (icon) {
    inputPropsObject.startAdornment = (
      <InputAdornment position="start">
        <FontAwesomeIcon icon={icon} size="sm" />
      </InputAdornment>
    )
  }

  let specificProps = {}

  if (isNumericFormat) {
    const NumericComponent =
      formatType === 'integer' ? IntegerFormatCustom : NumericFormatCustom

    inputPropsObject.inputComponent = NumericComponent

    specificProps = {
      InputProps: inputPropsObject,
    }
  } else if (mask === 'cpfCnpj') {
    inputPropsObject.inputComponent = CpfCnpjMaskCustom

    specificProps = {
      InputProps: inputPropsObject,
    }
  } else if (mask === 'phone') {
    inputPropsObject.inputComponent = PhoneMaskCustom

    specificProps = {
      InputProps: inputPropsObject,
    }
  } else if (mask) {
    inputPropsObject.inputComponent = InputMaskCustom

    specificProps = {
      InputProps: inputPropsObject,
      inputProps: {
        ...commonProps.inputProps,
        mask: mask,
      },
    }
  } else if (type === 'password') {
    specificProps = {
      type: isPasswordVisible ? 'text' : 'password',
      InputProps: {
        ...inputPropsObject,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={togglePasswordVisibility}
              edge="end"
              disabled={disabled || readOnly}>
              <FontAwesomeIcon icon={isPasswordVisible ? faEyeSlash : faEye} />
            </IconButton>
          </InputAdornment>
        ),
      },
    }
  } else {
    specificProps = {
      type,
      InputProps: inputPropsObject,
    }
  }

  return (
    <Box mb="4px">
      <StyledTextField
        {...commonProps}
        {...specificProps}
        size="small"
        label={label}
        defaultValue={defaultValue}
        slotProps={{
          inputLabel: {
            shrink: true,
          },
        }}
      />
    </Box>
  )
}

export default InputField
